import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PermitPreview = ({ 
  formData, 
  selectedType, 
  assignedPersonnel, 
  selectedHazards, 
  selectedEquipment,
  approvalLevels 
}) => {
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'Not specified';
    const date = new Date(dateTimeString);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border sticky top-6">
      <div className="bg-primary/5 border-b border-border p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
            <Icon name="Eye" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Permit Preview</h3>
            <p className="text-xs text-muted-foreground">Live preview of your permit</p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
        <div className="space-y-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Permit Type</p>
            <p className="font-medium text-foreground">{selectedType || 'Not selected'}</p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1">Work Title</p>
            <p className="font-medium text-foreground">{formData?.workTitle || 'Not specified'}</p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1">Location</p>
            <p className="text-sm text-foreground">
              {formData?.primaryLocation || 'Not specified'}
              {formData?.buildingArea && ` - ${formData?.buildingArea}`}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Start Time</p>
              <p className="text-sm text-foreground">{formatDateTime(formData?.startDateTime)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">End Time</p>
              <p className="text-sm text-foreground">{formatDateTime(formData?.endDateTime)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Duration</p>
              <p className="text-sm text-foreground">{formData?.estimatedDuration || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Workers</p>
              <p className="text-sm text-foreground">{formData?.numberOfWorkers || '0'}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-4">
          <p className="text-xs text-muted-foreground mb-3">Assigned Personnel</p>
          {assignedPersonnel?.length > 0 ? (
            <div className="space-y-2">
              {assignedPersonnel?.slice(0, 3)?.map((person) => (
                <div key={person?.id} className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full overflow-hidden bg-muted flex-shrink-0">
                    <Image
                      src={person?.avatar}
                      alt={person?.avatarAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-sm text-foreground truncate">{person?.name}</p>
                </div>
              ))}
              {assignedPersonnel?.length > 3 && (
                <p className="text-xs text-muted-foreground pl-8">
                  +{assignedPersonnel?.length - 3} more
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No personnel assigned</p>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <p className="text-xs text-muted-foreground mb-3">Identified Hazards</p>
          {selectedHazards?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {selectedHazards?.slice(0, 5)?.map((hazardId) => (
                <span
                  key={hazardId}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning text-xs rounded-md"
                >
                  <Icon name="AlertTriangle" size={12} />
                  Hazard
                </span>
              ))}
              {selectedHazards?.length > 5 && (
                <span className="text-xs text-muted-foreground">
                  +{selectedHazards?.length - 5} more
                </span>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No hazards identified</p>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <p className="text-xs text-muted-foreground mb-3">Safety Equipment</p>
          {selectedEquipment?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {selectedEquipment?.slice(0, 4)?.map((equipmentId) => (
                <span
                  key={equipmentId}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs rounded-md"
                >
                  <Icon name="Shield" size={12} />
                  Equipment
                </span>
              ))}
              {selectedEquipment?.length > 4 && (
                <span className="text-xs text-muted-foreground">
                  +{selectedEquipment?.length - 4} more
                </span>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No equipment selected</p>
          )}
        </div>

        {approvalLevels?.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="GitBranch" size={16} className="text-primary" />
              Approval Hierarchy
            </h4>
            <div className="space-y-2">
              {approvalLevels?.map((level, index) => {
                const getLevelName = (lvl) => {
                  switch (lvl) {
                    case 1: return 'Permit Issuer';
                    case 2: return 'Permit Acceptor';
                    case 3: return 'Safety Review';
                    case 4: return 'Management Approval';
                    default: return `Level ${lvl}`;
                  }
                };

                return (
                  <div 
                    key={level?.level} 
                    className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border"
                  >
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 border border-primary text-primary font-semibold text-xs flex-shrink-0">
                      {level?.level}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-muted-foreground">
                        {getLevelName(level?.level)}
                      </p>
                      {level?.approver ? (
                        <p className="text-sm font-medium text-foreground truncate">
                          {level?.approver?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">Not assigned</p>
                      )}
                    </div>
                    <div className="px-2 py-1 bg-warning/10 text-warning text-xs font-semibold rounded">
                      Pending
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-accent/5 border border-accent/20 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              This preview shows how your permit will appear once submitted. Complete all required fields to enable submission.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermitPreview;